import { JobDtoType } from '../domain/jobs';
import { IConnectionAttendeeDTO } from './connections.dao';
import { IEventRequestParams, IFileParams, IImageWithObjectKey, IImageWithUrl, IPaginationParams, ISortRequestParams } from './shared.dto';
import { IUserDTO } from './user-groups.dao';

abstract class IAttendeesDAO {
    public abstract getAttendees(params: IGetAttendeesRequestParams): Promise<IAttendeesListDTO>;
    public abstract getAttendee(params: IEventRequestParams & { attendeeId: number }): Promise<IAttendeeDetailsDTO>;
    public abstract getAttendeesCSV(params: IEventRequestParams): void;
    public abstract generateAccessCodes(params: IEventRequestParams): Promise<JobDtoType>;
    public abstract updateAttendee(attendeeDetails: IAttendeeDetailsDTO & IEventRequestParams): Promise<IAttendeeDetailsDTO>;
    public abstract attendeeBulkUpdate(params: IEventRequestParams & IAttendeeBulkUpdateDTO): Promise<JobDtoType>;
    public abstract createAttendee(attendeeDetails: ICreateAttendeeDTO & IEventRequestParams): Promise<IAttendeeDetailsDTO>;
    public abstract getParticipants(params: IEventRequestParams & ISortRequestParams<TGetAttendeesSortKeys>): Promise<IAttendeesListDTO>;
    public abstract deleteAttendee(params: IDeleteAttendeeDTO): Promise<JobDtoType>;
    public abstract importAccessCodes(params: IFileParams): Promise<JobDtoType>;
    public abstract generateOrganizationsLinks(params: IEventRequestParams): Promise<JobDtoType>;
    public abstract updateBiography(params: IEventRequestParams & { attendeeId: number; biography: string }): Promise<IAttendeeDetailsDTO>;
}

interface IAttendeeListItemDTO {
    attendeeId: number;
    organizationName: string;
    organizationTicker: string;
    organizationType: string;
    firstName: string;
    lastName: string;
    salutation: string;
    assistantEmail: string;
    title: string;
    email: string;
    phoneNumber: string;
    accessCode: string;
    customFieldValues: { [customFieldId: number]: string };
    registrationDate: string;
    registrationStatus: AttendeeRegistrationStatus;
    attendanceMethod: AttendeeAttendanceMethod;
    eventSubscriptionDate: string;
}

type TGetAttendeesSortKeys = keyof Omit<IAttendeeListItemDTO, 'attendeeId'>
    | 'attendeeId'
    | 'organization.name'
    | 'lastName'
    | 'organization.organizationType';

type IGetAttendeesRequestParams =
    IEventRequestParams &
    ISortRequestParams<TGetAttendeesSortKeys> &
    IAttendeesFilterParams &
    IPaginationParams;

interface IAttendeesFilterParams {
    query?: string;
    registrationStatuses?: string;
    attendanceMethods?: string;
}

interface ICreateAttendeeDTO {
    organizationName?: string;
    firstName: string;
    lastName: string;
    title: string;
    email: string;
    assistantEmail: string;
    phoneNumber: string;
    accessCode: string;
    salutation: string;
    externalAttendeeId: string;
    emailNotProvided: boolean;
    priorityInOrganization: number;
    speaker: boolean;
    biography: string;
    profileImage?: IImageWithObjectKey;
    profileImageWithUrl?: IImageWithUrl;
    registrationStatus: AttendeeRegistrationStatus;
    attendanceMethod: AttendeeAttendanceMethod | string;
    podiumQuestionAnswerPriorityType: PodiumQuestionAnswerPriorityType | null;
}

interface IAttendeeDetailsDTO extends ICreateAttendeeDTO {
    attendeeId: number;
    organizationId: number;
    sendRegistrationEmail?: boolean;
    podiumQaNote?: string;
    oeContactId?: number | null;
}
interface IAttendeeBulkUpdateDTO {
    newStatus: AttendeeRegistrationStatus;
    attendeeIds: number[];
    sendRegistrationEmail?: boolean;
}
interface IControlAttendeeDetailsDTO {
    previousMeetingAttendeeNotes: IAttendeeNote[];
    organizationName?: string;
    firstName: string;
    lastName: string;
    title: string;
    email: string;
    phoneNumber: string;
    accessCode: string;
    salutation: string;
    externalAttendeeId: string;
    attendeeId: number;
    organizationId: number;
}

interface IDeleteAttendeeDTO extends IEventRequestParams {
    attendeeId: number;
}

interface IAttendeesListDTO {
    content: IAttendeeListItemDTO[];
    totalElements: number;
}

interface IMeetingListAttendeeItemDTO {
    organization: string;
    firstName: string;
    lastName: string;
}

interface IParticipantDTO extends IMeetingListAttendeeItemDTO {
    participantId: number;
}

enum MeetingAttendeeStatus {
    CONFIRMED = 'CONFIRMED',
    JOINED = 'JOINED',
    CANCELLED = 'CANCELLED',
    NOSHOW = 'NO_SHOW',
    WALK_IN = 'WALK_IN'
}

enum MeetingAttendeeJoinMethod {
    AUDIO = 'AUDIO',
    VIDEO = 'VIDEO',
    IN_PERSON = 'IN_PERSON'
}

enum MeetingAttendeeIssue {
    LATE = 'LATE',
    BAD_AUDIO = 'BAD_AUDIO',
    BAD_VIDEO = 'BAD_VIDEO',
    CONNECTION_DROPPED = 'CONNECTION_DROPPED'
}

interface IAttendeeNote {
    meetingStartDateTime: Date;
    note: string;
}

enum AttendeeRole {
    Presenter = 'Presenter',
    Participant = 'Participant',
    Host = 'Host',
    Support = 'Support',
    Registrant = 'Registrant'
}

enum AttendeeRegistrationStatus {
    APPROVED = 'APPROVED',
    PENDING = 'PENDING',
    DENIED = 'DENIED'
}

enum AttendeeAttendanceMethod {
    IN_PERSON = 'IN_PERSON',
    VIRTUAL = 'VIRTUAL',
    BOTH = 'BOTH'
}

interface IAttendeeAttendanceMethod {
    value: AttendeeAttendanceMethod;
    label: string;
}

export const AttendeeRegistrationStatusLabel = {
    [AttendeeRegistrationStatus.APPROVED]: 'Approved',
    [AttendeeRegistrationStatus.PENDING]: 'Pending',
    [AttendeeRegistrationStatus.DENIED]: 'Denied',
};

interface IAttendeeInput {
    meetingAttendeeId: number;
    status: MeetingAttendeeStatus;
    joinMethod?: MeetingAttendeeJoinMethod;
    meetingAttendeeNote?: string;
    issues?: MeetingAttendeeIssue[];
    notes?: IAttendeeNote[];
    previousMeetingAttendeeNotes?: IAttendeeNote[];
    isLate?: boolean;
}

interface IAttendeeDTO extends Partial<IAttendeeInput> {
    attendeeId: number;
    title?: string;
    phoneNumber?: string;
    email?: string;
    firstName: string;
    lastName: string;
    meetingConnectionId?: number;
    presenterPortalAccessEnabled?: boolean;
    biography?: string;
    profileImageWithUrl?: IImageWithUrl;
}

interface IFilteredAttendee extends IAttendeeDTO {
    markedName?: string | JSX.Element;
    markedOrganization?: string | JSX.Element;
    markedPhone?: string | JSX.Element;
}

interface IPresentersConnectionDTO {
    type: string;
    knovioName?: string;
    embedCodeId?: string;
    connectionId?: string;
    meetingConnectionId: number;
}

interface IAttendeeOrganizationDTO {
    meetingOrganizationId?: number;
    externalOrganizationId?: string;
    name: string;
    organizationId: number;
    attendees: IConnectionAttendeeDTO[];
    organizationType?: string;
    organizationTicker?: string;
    meetingConnectionId?: number;
    firstMeetingInTheDay?: boolean;
    chaperones?: IUserDTO[];
    presenterPortalAccessEnabled?: boolean;
}

interface IFilteredOrganization extends Omit<IAttendeeOrganizationDTO, 'attendees'> {
    attendees: IFilteredAttendee[];
    markedOrganization?: string | JSX.Element;
}

interface IAttendeeOrganizationListDTO {
    content: IAttendeeOrganizationDTO[];
    totalElements: number;
}

interface IParticipantDTO {
    organization: string;
    firstName: string;
    lastName: string;
    participantId: number;
    meetingParticipantId?: number;
}
interface IButtonPropertyDTO {
    text: string;
    disabled: boolean;
}

enum AttendeeLinkType {
    MEETING_LINK_WITH_EMBEDDED_PASSCODE = 'MEETING_LINK_WITH_EMBEDDED_PASSCODE',
    PASSPORT_LINK = 'PASSPORT_LINK'
}

enum PodiumQuestionAnswerPriorityType {
    STAR = 'STAR',
    RED_FLAG = 'RED_FLAG'
}

export {
    IAttendeeListItemDTO,
    IAttendeesDAO,
    IAttendeesListDTO,
    IParticipantDTO,
    IAttendeeDetailsDTO,
    ICreateAttendeeDTO,
    TGetAttendeesSortKeys,
    IMeetingListAttendeeItemDTO,
    IAttendeeOrganizationDTO,
    IFilteredOrganization,
    IAttendeeDTO,
    IFilteredAttendee,
    IAttendeeOrganizationListDTO,
    MeetingAttendeeStatus,
    IAttendeeInput,
    MeetingAttendeeJoinMethod,
    MeetingAttendeeIssue,
    IDeleteAttendeeDTO,
    IAttendeeNote,
    IControlAttendeeDetailsDTO,
    AttendeeRole,
    IPresentersConnectionDTO,
    IButtonPropertyDTO,
    AttendeeRegistrationStatus,
    IAttendeeBulkUpdateDTO,
    AttendeeAttendanceMethod,
    IAttendeeAttendanceMethod,
    AttendeeLinkType,
    PodiumQuestionAnswerPriorityType
};
